import { excludeNullOrUndefined } from 'checkout/utils'
import createSelector from 'lib/web/createSelector'
import { RefundProtectQuoteRequest } from 'api/insurance'
import { getTotalsWithoutInsurance } from 'checkout/selectors/payment/checkout'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { checkoutAccommodationGroupingKey, checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { areOccupantsEqual, mergeOccupants } from 'lib/offer/occupancyUtils'
import { getBookingProtectionEnabledCheckoutItems } from '../view/bookingProtection'

const getBookingProtectionOccupantsByAccommodationGroup = createSelector(
  getBookingProtectionEnabledCheckoutItems,
  (items) => {
    if (items.length > 0) {
      const itemMap = groupBy(
        items, checkoutAccommodationGroupingKey)

      return Array.from(itemMap.values()).map((itemList) => {
        const occupants = itemList
          .map(item => 'occupancy' in item ? item.occupancy : null)
          .filter(excludeNullOrUndefined)
        return mergeOccupants(occupants)
      })
    }
  },
)

export const areBookingProtectionOccupantsConsistent = createSelector(
  getBookingProtectionOccupantsByAccommodationGroup,
  (occupants): boolean => {
    return occupants ? areOccupantsEqual(...occupants) : true // if no occupants, that's consistent
  },
)

export const getBookingProtectionOccupantsFromCartItems = createSelector(
  getBookingProtectionOccupantsByAccommodationGroup,
  areBookingProtectionOccupantsConsistent,
  (occupants, isConsistent) => {
    if (isConsistent && occupants) {
      return occupants[0]
    }
  },
)

export const getBookingProtectionStartDateFromCart = createSelector(
  checkoutAccommodationOfferView,
  (accommodationViews) => {
    if (!accommodationViews.hasRequiredData) { return null }

    const accommodationDates = accommodationViews.data.map(item => ({
      startDate: item.startDate,
    }))

    const dates = [...accommodationDates]

    if (dates.length) {
      return moment.min(dates.map(date => moment(date.startDate, ISO_DATE_FORMAT)))
    }
  },
)

export const getBookingProtectionCoverAmountFromCart = createSelector(
  getTotalsWithoutInsurance,
  (totals): number | undefined => {
    if (totals.hasRequiredData) {
      const {
        paidPrice = 0,
        price = 0,
        surcharge = 0,
        otherFees = {},
      } = totals.data
      return paidPrice + price + surcharge + sum(Object.values(otherFees))
    }
  },
)

export const getBookingProtectionQuoteRequest = createSelector(
  getBookingProtectionOccupantsFromCartItems,
  getBookingProtectionCoverAmountFromCart,
  getBookingProtectionStartDateFromCart,
  (state: App.State) => state.checkout.cart.currencyCode,
  (occupants, coverAmount, startDate, currencyCode): RefundProtectQuoteRequest | null => {
    if (!occupants || !coverAmount || !startDate) return null

    return {
      brand: 'luxuryescapes',
      currencyCode,
      eventTravelDateTime: startDate.format('YYYY-MM-DDTHH:mm:ss.SSSSSSZZ'),
      totalValue: coverAmount,
      numberOfTickets: occupants.adults + (occupants.children ?? 0) + (occupants.infants ?? 0),
    }
  },
)
